import Dexie, { type EntityTable } from "dexie";
import type { Money } from "../../domain/money/money";
import type { IsoTimestamp, BusinessDate } from "../../domain/time";

/**
 * The terminal's local replica and outbox.
 *
 * This is not a cache. It is where a sale is COMMITTED — the UI confirms from
 * this database, not from a server response (Principle I, FR-043). The network
 * is an optimisation applied afterwards.
 *
 * Everything a terminal needs to trade lives here: catalog, staff, open shift,
 * counters, and the queue of mutations awaiting upload.
 */

// ---------------------------------------------------------------------------
// Catalog (replicated, read-only on the terminal)
// ---------------------------------------------------------------------------

export interface LocalCategory {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
  /** null for a top-level tab; otherwise the tab this section sits under. */
  parentId: string | null;
}

export interface LocalMenuItem {
  id: string;
  categoryId: string;
  name: string;
  description: string | null;
  imageKey: string | null;
  sortOrder: number;
  statutoryDiscountEligible: boolean;
  available: boolean;
  retiredAt: IsoTimestamp | null;
}

export interface LocalVariant {
  id: string;
  menuItemId: string;
  name: string;
  isDefault: boolean;
  sortOrder: number;
  retiredAt: IsoTimestamp | null;
  /** Price in force at hydration time. Historical prices live on the sale. */
  price: Money;
}

export interface LocalModifierGroup {
  id: string;
  menuItemId: string;
  name: string;
  minSelections: number;
  maxSelections: number;
  required: boolean;
  sortOrder: number;
}

export interface LocalModifier {
  id: string;
  modifierGroupId: string;
  name: string;
  sortOrder: number;
  retiredAt: IsoTimestamp | null;
  priceAdjustment: Money;
}

// ---------------------------------------------------------------------------
// People (no PIN material is ever replicated except the hash needed for
// offline manager authorization — FR-052 still forbids displaying or exporting it)
// ---------------------------------------------------------------------------

export interface LocalStaff {
  id: string;
  name: string;
  roleId: string;
  roleName: string;
  permissions: string[];
  canAuthorize: boolean;
  active: boolean;
  pinHash: string;
  pinSalt: string;
  pinIterations: number;
}

// ---------------------------------------------------------------------------
// Journal (local, append-only, mirrors D1)
// ---------------------------------------------------------------------------

export type SaleStatus = "finalized" | "voided" | "refunded" | "partially_refunded";

export interface LocalSale {
  id: string;
  documentNumber: string;
  series: string;
  sequence: number;
  terminalId: string;
  shiftId: string;
  staffId: string;
  orderType: string;
  customerName: string | null;
  status: SaleStatus;
  subtotal: Money;
  discountTotal: Money;
  statutoryDiscountTotal: Money;
  taxableAmount: Money;
  taxExemptAmount: Money;
  zeroRatedAmount: Money;
  taxAmount: Money;
  total: Money;
  taxRegime: string;
  taxRateBp: number;
  activeModules: string[];
  businessDate: BusinessDate;
  createdAt: IsoTimestamp;
  /** Set once the server has acknowledged. Drives the pending indicator. */
  syncedAt: IsoTimestamp | null;
}

export interface LocalSaleLine {
  id: string;
  saleId: string;
  lineNumber: number;
  menuItemId: string;
  variantId: string | null;
  itemNameSnapshot: string;
  variantNameSnapshot: string | null;
  quantity: number;
  unitPrice: Money;
  modifierTotal: Money;
  lineSubtotal: Money;
  lineDiscount: Money;
  lineTotal: Money;
  statutoryEligible: boolean;
  note: string | null;
  modifiers: Array<{
    id: string;
    modifierId: string;
    nameSnapshot: string;
    priceAdjustment: Money;
  }>;
}

export interface LocalTender {
  id: string;
  saleId: string;
  type: string;
  amount: Money;
  reference: string | null;
  changeGiven: Money;
  createdAt: IsoTimestamp;
}

export interface LocalDiscount {
  id: string;
  saleId: string;
  saleLineId: string | null;
  type: "promotional" | "senior_citizen" | "pwd";
  basis: string | null;
  rateBp: number;
  amount: Money;
  coveredCustomers: number | null;
  totalCustomers: number | null;
  cardholderName: string | null;
  cardholderId: string | null;
  authorizedBy: string | null;
  /** The module that granted it (a line grant). Absent for core discounts. Not indexed. */
  grantedByModule?: string;
  createdAt: IsoTimestamp;
}

// ---------------------------------------------------------------------------
// Shifts
// ---------------------------------------------------------------------------

export interface LocalShift {
  id: string;
  terminalId: string;
  openedBy: string;
  openedAt: IsoTimestamp;
  openingFloat: Money;
  closedBy: string | null;
  closedAt: IsoTimestamp | null;
  countedCash: Money | null;
  expectedCash: Money | null;
  variance: Money | null;
  varianceReason: string | null;
  varianceAuthorizedBy: string | null;
  status: "open" | "closed";
  businessDate: BusinessDate;
}

export interface LocalCashMovement {
  id: string;
  shiftId: string;
  direction: "in" | "out";
  amount: Money;
  reason: string;
  staffId: string;
  createdAt: IsoTimestamp;
}

/**
 * The CORE cash-control record: that a person was authorized to reach the cash,
 * and why. Written whether or not any peripheral module exists (FR-041b).
 * The peripherals module's hardware-attempt record is separate and references
 * this one.
 */
export interface LocalAuditEntry {
  id: string;
  entityType: string;
  entityId: string | null;
  action: string;
  shiftId: string | null;
  staffId: string;
  authorizedBy: string | null;
  reason: string | null;
  detail: Record<string, unknown> | null;
  createdAt: IsoTimestamp;
}

/**
 * Corrections (T157, FR-022).
 *
 * These stand BESIDE the sale, never inside it. A sale's displayed status is
 * derived from whether these rows exist — `LocalSale.status` is written once at
 * finalisation and never touched again, exactly as the server treats it.
 */
export interface LocalVoid {
  id: string;
  saleId: string;
  shiftId: string;
  reason: string;
  staffId: string;
  authorizedBy: string;
  createdAt: IsoTimestamp;
  syncedAt: IsoTimestamp | null;
}

export interface LocalRefund {
  id: string;
  saleId: string;
  shiftId: string;
  reason: string;
  staffId: string;
  authorizedBy: string;
  totalAmount: Money;
  /** Cash out of the drawer, as opposed to a reversal on the original tender. */
  isCash: boolean;
  createdAt: IsoTimestamp;
  syncedAt: IsoTimestamp | null;
}

export interface LocalRefundLine {
  id: string;
  refundId: string;
  saleLineId: string;
  quantity: number;
  amount: Money;
}

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export type OutboxState = "pending" | "in_flight" | "acked" | "rejected";

export interface OutboxEnvelope {
  opId: string;
  type: string;
  payload: unknown;
  createdAt: IsoTimestamp;
  terminalId: string;
  attempts: number;
  state: OutboxState;
  lastError: string | null;
  nextAttemptAt: IsoTimestamp | null;
}

/**
 * Per-series document number counter.
 *
 * Advanced ONLY inside the transaction that commits a sale. That is what makes
 * numbering gap-free (FR-019) and what stops an abandoned cart consuming a
 * number (FR-015).
 */
export interface LocalCounter {
  series: string;
  nextSequence: number;
}

export interface LocalMeta {
  key: string;
  value: unknown;
}

export interface LocalCart {
  id: string;
  label: string | null;
  state: unknown;
  updatedAt: IsoTimestamp;
}

/**
 * A record produced by a MODULE, held locally until it syncs.
 *
 * Deliberately generic rather than a `drawerEvents` table. Core provides the
 * storage; it does not know what a drawer is. A table named for one module
 * would put that module's vocabulary in core, and the next module would want
 * its own table, and the schema would grow a version bump per module —
 * migrating every terminal's IndexedDB to add something core never reads.
 *
 * `moduleId` and an opaque `record` keep the boundary intact: peripherals
 * writes drawer events here, KDS could write bumps, and core stores both
 * without importing either (Principle III).
 */
export interface LocalModuleRecord {
  opId: string;
  moduleId: string;
  kind: string;
  shiftId: string | null;
  record: unknown;
  createdAt: IsoTimestamp;
}

// ---------------------------------------------------------------------------
// WiFi voucher tickets (specs/003-wifi-voucher-tickets)
// ---------------------------------------------------------------------------

/**
 * The pool replica. Full-replaced on each pull from `GET /api/wifi-vouchers`
 * (research.md R3) — never merged, never partially updated locally, so a
 * rotation on the server is a clean overwrite here too.
 */
export interface LocalWifiVoucher {
  code: string;
  used: boolean;
  poolBatch: string;
}

/**
 * Local history of what THIS terminal has handed out. The server's
 * `per_wifi_voucher_issuance` remains the durable copy of record; this is
 * what lets the till itself answer "what did we already give out" without a
 * round trip, and what the outbox drains from.
 */
export interface LocalWifiVoucherIssuance {
  id: string;
  code: string;
  terminalId: string;
  staffId: string;
  createdAt: IsoTimestamp;
}

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export class PosDatabase extends Dexie {
  categories!: EntityTable<LocalCategory, "id">;
  menuItems!: EntityTable<LocalMenuItem, "id">;
  variants!: EntityTable<LocalVariant, "id">;
  modifierGroups!: EntityTable<LocalModifierGroup, "id">;
  modifiers!: EntityTable<LocalModifier, "id">;
  staff!: EntityTable<LocalStaff, "id">;
  sales!: EntityTable<LocalSale, "id">;
  saleLines!: EntityTable<LocalSaleLine, "id">;
  tenders!: EntityTable<LocalTender, "id">;
  discounts!: EntityTable<LocalDiscount, "id">;
  shifts!: EntityTable<LocalShift, "id">;
  cashMovements!: EntityTable<LocalCashMovement, "id">;
  auditEntries!: EntityTable<LocalAuditEntry, "id">;
  voids!: EntityTable<LocalVoid, "id">;
  refunds!: EntityTable<LocalRefund, "id">;
  refundLines!: EntityTable<LocalRefundLine, "id">;
  outbox!: EntityTable<OutboxEnvelope, "opId">;
  counters!: EntityTable<LocalCounter, "series">;
  meta!: EntityTable<LocalMeta, "key">;
  carts!: EntityTable<LocalCart, "id">;
  moduleRecords!: EntityTable<LocalModuleRecord, "opId">;
  wifiVouchers!: EntityTable<LocalWifiVoucher, "code">;
  wifiVoucherIssuances!: EntityTable<LocalWifiVoucherIssuance, "id">;

  /**
   * Domain-neutral by design. A deployment for a different business must not
   * need a different database name, and core must carry no brand literal
   * (Principle VI) — the boundary checker enforces both.
   */
  constructor(name = "pos-terminal") {
    super(name);

    this.version(1).stores({
      categories: "id, sortOrder",
      menuItems: "id, categoryId, sortOrder, available",
      variants: "id, menuItemId",
      modifierGroups: "id, menuItemId",
      modifiers: "id, modifierGroupId",
      staff: "id, active",
      sales: "id, documentNumber, shiftId, businessDate, syncedAt, [series+sequence]",
      saleLines: "id, saleId",
      tenders: "id, saleId",
      discounts: "id, saleId",
      shifts: "id, status, businessDate",
      cashMovements: "id, shiftId",
      auditEntries: "id, action, shiftId",
      outbox: "opId, state, createdAt",
      counters: "series",
      meta: "key",
      carts: "id, updatedAt",
    });

    // v2 adds module-owned records. Dexie applies this to an existing database
    // without touching the v1 stores, so a terminal mid-outage upgrades without
    // any risk to sales it has not yet synced.
    this.version(2).stores({
      moduleRecords: "opId, moduleId, shiftId, createdAt",
    });

    // v3 adds corrections. Additive like v2: a terminal that upgrades
    // mid-outage keeps every unsynced sale exactly where it was.
    this.version(3).stores({
      voids: "id, saleId, shiftId, syncedAt",
      refunds: "id, saleId, shiftId, syncedAt",
      refundLines: "id, refundId, saleLineId",
    });

    // v4 adds WiFi voucher tickets (specs/003-wifi-voucher-tickets). Additive,
    // same as v2/v3: nothing here touches an existing store.
    this.version(4).stores({
      wifiVouchers: "code, used",
      wifiVoucherIssuances: "id, code, terminalId, createdAt",
    });
  }
}

export const db = new PosDatabase();

// ---------------------------------------------------------------------------
// Meta helpers
// ---------------------------------------------------------------------------

export const META_KEYS = {
  terminalId: "terminal.id",
  series: "terminal.series",
  sessionToken: "auth.sessionToken",
  syncCursor: "sync.cursor",
  serverTimeOffsetMs: "sync.serverTimeOffsetMs",
  /** Last clock-skew reading. Records are flagged, never silently corrected. */
  clockSkew: "sync.clockSkew",
  businessProfile: "config.businessProfile",
  activeModules: "config.activeModules",
} as const;

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

/**
 * Ask the browser to keep this data.
 *
 * Without it, iOS in particular will evict IndexedDB under storage pressure —
 * which for this application means losing sales that have not yet synced.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted()) return true;
  return navigator.storage.persist();
}
